import React, { memo, useEffect } from 'react'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { BidResponse } from 'apps/fleet-planner/types'
import { CheckFilledIcon, RefreshIcon } from 'transfix-icons'
import { Button } from 'transfix-ui'
import { formatCurrency } from 'utils/formatCurrency'

interface IBidCounterModalProps {
  updatedBid?: BidResponse
  isOpen: boolean
  onBookNow: () => void
  onClose: () => void
}

const useCounterOfferStyles = makeStyles(theme => ({
  counterOfferIcon: {
    backgroundColor: brandColors.skyBlue2,
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 2,
    color: brandColors.skyBlue7,
  },
  counterOfferButton: {
    backgroundColor: 'transparent',
  },
  boundingBorder: {
    borderColor: brandColors.coolGray2,
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  mobileIcon: {
    color: '#00C28A',
    height: '64px',
    width: '64px',
  },
}))

export const BidCounteredModal = memo<IBidCounterModalProps>(
  ({ isOpen, onBookNow, onClose, updatedBid }) => {
    const { isMobile } = useBreakpointContext()
    const classes = useCounterOfferStyles()
    const { trackEvent } = useFMSAnalytics()

    // Track when counteroffers are presented to a user
    useEffect(() => {
      if (isOpen) {
        trackEvent('Bid Countered Modal', 'VIEWED_COUNTER_OFFER_ON_LOAD_MODAL')
      }
      // don't want to include trackEvent here, will result in reduntant events
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const bookNowHandler = () => {
      trackEvent('Bid Countered Modal', 'ACCEPTED_COUNTER_OFFER_ON_LOAD_MODAL')
      onBookNow()
    }

    return (
      <FullPageOverlay isOpen={isOpen} onClose={onClose} fullScreen={isMobile} maxWidth='xs'>
        <Box
          data-test='bid-countered-modal'
          display='flex'
          flexDirection='column'
          justifyContent='space-between'
          height='100%'
          p={1}>
          <Box display='flex' flexDirection='column' justifyContent='center'>
            {isMobile ? (
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                textAlign='center'
                pb={4}>
                <Box pb={1}>
                  <CheckFilledIcon className={classes.mobileIcon} />
                </Box>
                <Typography variant='h2'>Your bid was submitted!</Typography>
                <Typography variant='body1'>
                  We’ll let you know if your bid gets accepted.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant='h3'>Your bid was submitted!</Typography>
                <Typography variant='body2'>
                  We’ll let you know if your bid gets accepted.
                </Typography>
              </>
            )}

            <Box my={3} py={2} px={3} className={classes.boundingBorder}>
              <Typography variant='body2'>
                Would you accept our counter offer? If you don’t accept now, the offer will still be
                available.
              </Typography>

              <Box display='flex' alignItems='center' justifyContent='space-between' py={2}>
                <Box display='flex' alignItems='center'>
                  <RefreshIcon size='xlarge' className={classes.counterOfferIcon} />
                  <Typography className='pl2' variant='h3'>
                    Counter Offer
                  </Typography>
                </Box>
                <Typography data-test='bid-countered-modal--counterbid-amount' variant='h3'>
                  {formatCurrency(updatedBid?.counterBidAmount)}
                </Typography>
              </Box>
              <Box pt={1}>
                <Button
                  data-test='bid-countered-modal--book-now-button'
                  fullWidth
                  onClick={bookNowHandler}
                  size='large'>
                  Accept Counter Offer
                </Button>
              </Box>
            </Box>
          </Box>

          <Button
            fullWidth
            color='secondary'
            data-test='bid-countered-modal--skip-and-close-button'
            onClick={onClose}
            size='large'>
            Skip and Close
          </Button>
        </Box>
      </FullPageOverlay>
    )
  }
)
